import { useLayoutEffect, useState, type RefObject } from "react";

/**
 * How tall a pane that wants «the rest of the screen» should be.
 *
 * The arithmetic used to be written out in CSS: a viewport unit minus a
 * header, minus a tab bar, minus a safe area, minus a keyboard. Every term was
 * this app's belief about how some platform reports something, and two of them
 * were wrong on one phone in ways that took three tries to tell apart.
 *
 * So the pane asks instead of assuming. Where it starts is already on screen —
 * `top` absorbs the notch, the header and any notice above it without knowing
 * that any of them exist. What has to stay free below it is the padding and
 * margin the layout already reserves on `<main>` for the bar, the home
 * indicator and a keyboard. Neither number names a device.
 */
export function usePaneFill(ref: RefObject<HTMLElement | null>): string | null {
  const [fill, setFill] = useState<string | null>(null);

  useLayoutEffect(() => {
    const pane = ref.current;
    if (!pane) return;
    const main = pane.closest("main");

    const measure = () => {
      const top = pane.getBoundingClientRect().top + window.scrollY;
      const below = main ? getComputedStyle(main) : null;
      const reserve = below
        ? (parseFloat(below.paddingBottom) || 0) + (parseFloat(below.marginBottom) || 0)
        : 0;
      setFill(`calc(100dvh - ${Math.round(top + reserve)}px)`);
    };

    measure();

    /*
     * The chrome around a pane moves by way of custom properties on the root —
     * that is the whole of the signal, and it arrives as an attribute change
     * rather than a resize, which is why watching boxes alone missed it.
     */
    const watchers: Array<() => void> = [];
    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver(measure);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["style"],
      });
      watchers.push(() => observer.disconnect());
    }
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(document.documentElement);
      watchers.push(() => observer.disconnect());
    }
    window.addEventListener("resize", measure);
    watchers.push(() => window.removeEventListener("resize", measure));

    return () => {
      for (const stop of watchers) stop();
    };
  }, [ref]);

  return fill;
}
